import { AnimatePresence, motion } from "motion/react";
import { cn, normalizeText } from "../../utils/format";

export interface StackLayer {
  id: number;
  name: string;
  /** Categoria do ingrediente (pao, carne, queijo, salada, molho, extra). */
  slug: string;
  quantity: number;
}

interface LayerStyle {
  className: string;
}

/** Aparência de cada camada: por nome (quando conhecido) ou pela categoria. */
const styleFor = (layer: StackLayer): LayerStyle => {
  const name = normalizeText(layer.name);
  if (name.includes("alface") || name.includes("rucula")) return { className: "h-3 w-[96%] rounded-[40%] bg-[#6DBE45]" };
  if (name.includes("tomate")) return { className: "h-3 w-[84%] rounded-full bg-[#E53935]" };
  if (name.includes("picles")) return { className: "h-2 w-[80%] rounded-full bg-[#9CCC65]" };
  if (name.includes("cebola caramel")) return { className: "h-2.5 w-[86%] rounded-full bg-[#A1662F]" };
  if (name.includes("cebola")) return { className: "h-2 w-[82%] rounded-full bg-[#CE93D8]" };
  if (name.includes("bacon")) return { className: "h-2.5 w-[92%] rounded-sm bg-[repeating-linear-gradient(90deg,#B23A2A_0_10px,#E07A5F_10px_14px)]" };
  if (name.includes("ovo")) return { className: "h-4 w-[78%] rounded-full bg-[radial-gradient(circle,#FFB300_0_28%,#FFF8E1_30%)]" };
  if (name.includes("cogumelo")) return { className: "h-2.5 w-[80%] rounded-full bg-[#8D6E63]" };
  if (name.includes("onion")) return { className: "h-3.5 w-[90%] rounded-full border-4 border-[#E0A96D] bg-transparent" };
  if (name.includes("jalapeno") || name.includes("pimenta")) return { className: "h-2 w-[70%] rounded-full bg-[#43A047]" };
  if (name.includes("barbecue")) return { className: "h-1.5 w-[88%] rounded-full bg-[#6D2E12]" };
  if (name.includes("agridoce")) return { className: "h-1.5 w-[88%] rounded-full bg-[#F57C00]" };
  switch (layer.slug) {
    case "carne":
      return { className: "h-6 w-[94%] rounded-full bg-gradient-to-b from-[#7A4A2A] to-[#4E2C17] shadow-[inset_0_-3px_0_rgba(0,0,0,0.25)]" };
    case "queijo":
      return { className: "h-2.5 w-[100%] rounded-sm bg-gradient-to-b from-[#FFD54F] to-[#FFB300]" };
    case "molho":
      return { className: "h-1.5 w-[88%] rounded-full bg-[#FF8A65]" };
    default:
      return { className: "h-3 w-[88%] rounded-full bg-[#BCAAA4]" };
  }
};

const ORDER = ["salada", "carne", "queijo", "extra", "molho"];

/** Monta a pilha de baixo para cima: pão de baixo, salada, carnes intercaladas com queijo, extras, molho, pão de cima. */
const buildStack = (layers: StackLayer[]) => {
  const units = (slug: string) =>
    layers.filter((l) => l.slug === slug).flatMap((l) => Array.from({ length: l.quantity }, (_, i) => ({ ...l, key: `${l.id}-${i}` })));
  const meats = units("carne");
  const cheeses = units("queijo");
  const middle: Array<StackLayer & { key: string }> = [];
  const max = Math.max(meats.length, cheeses.length);
  for (let i = 0; i < max; i++) {
    if (meats[i]) middle.push(meats[i]);
    if (cheeses[i]) middle.push(cheeses[i]);
  }
  return [
    ...units("salada"),
    ...middle,
    ...ORDER.slice(3).flatMap(units),
    ...layers.filter((l) => !["pao", ...ORDER].includes(l.slug)).flatMap((l) => units(l.slug)),
  ];
};

export const BurgerStack = ({ layers, className }: { layers: StackLayer[]; className?: string }) => {
  const hasBread = layers.some((l) => l.slug === "pao");
  const fillings = buildStack(layers);
  const empty = layers.length === 0;

  const enter = { opacity: 0, y: -24, scale: 0.9 };
  const show = { opacity: 1, y: 0, scale: 1 };

  return (
    <div aria-hidden="true" className={cn("flex min-h-56 flex-col items-center justify-end", className)}>
      {empty ? (
        <div className="grid h-40 w-full max-w-[240px] place-items-center rounded-[2rem] border-2 border-dashed border-line text-center text-xs text-muted">
          Seu lanche aparece aqui
        </div>
      ) : (
        <div className="flex w-full max-w-[240px] flex-col items-center gap-[3px]">
          <AnimatePresence initial={false}>
            {hasBread && (
              <motion.div
                key="top-bun"
                layout
                initial={enter}
                animate={show}
                exit={{ opacity: 0, y: -20 }}
                className="relative h-14 w-full rounded-b-xl rounded-t-[999px] bg-gradient-to-b from-[#E8A84F] to-[#C77E2E] shadow-[inset_0_-4px_0_rgba(0,0,0,0.15)]"
              >
                {[18, 34, 50, 66, 82, 26, 58, 74].map((left, i) => (
                  <span
                    key={left + i}
                    className="absolute h-1 w-2 rounded-full bg-[#FFF3D6]"
                    style={{ left: `${left}%`, top: `${i < 5 ? 30 : 55}%`, transform: `rotate(${(i % 3) * 25 - 20}deg)` }}
                  />
                ))}
              </motion.div>
            )}
            {[...fillings].reverse().map((layer) => (
              <motion.div
                key={layer.key}
                layout
                initial={enter}
                animate={show}
                exit={{ opacity: 0, x: 40 }}
                transition={{ type: "spring", stiffness: 420, damping: 28 }}
                className={styleFor(layer).className}
              />
            ))}
            {hasBread && (
              <motion.div
                key="bottom-bun"
                layout
                initial={enter}
                animate={show}
                exit={{ opacity: 0, y: 20 }}
                className="h-8 w-full rounded-b-3xl rounded-t-md bg-gradient-to-b from-[#D99447] to-[#B86E28]"
              />
            )}
          </AnimatePresence>
          {!hasBread && <p className="mt-3 text-xs text-muted">Escolha o pão para fechar o lanche</p>}
        </div>
      )}
    </div>
  );
};
